import { type IUrlParams } from '@/types'
import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample, split } from 'effector'
import { createForm } from 'effector-forms'
import { and } from 'patronum'
import { epgRegionsModel } from '~/entities/epg/regions'
import { epgRegionsFeature } from '~/features/epg/regions'
import {
  getRegionIdFromSegment,
  selectEpgRegionModel,
} from '~/features/epg/regions/select'
import { landingRegionFeatureModel } from '~/features/landingRegion'
import { preferencesFeatureModel } from '~/features/preferences'
import { $route } from '~/processes/navigation'
import { PATH, ROUTE_NAME, SEGMENT } from '~/shared/constants'
import { F, T } from '~/shared/helpers'

export const init = createEvent<IUrlParams>()

const $initData = createStore<{
  regionId: number | null
} | null>(null).on(init, (_, params) => {
  return {
    regionId: getRegionIdFromSegment(params[SEGMENT.REGION]) ?? null,
  }
})

const $regionId = $initData.map((initData) => initData?.regionId ?? null)

// select region
export const select = selectEpgRegionModel.setRegion

// currently selected region
export const $selected = selectEpgRegionModel.$regionStorage

// check if region id is in url
export const checkUrl = createEvent()

sample({
  clock: [init, epgRegionsModel.getAllLiveRegionsFx.finally],
  source: epgRegionsModel.$regionsAlphabeticallySorted,
  filter: (regions) => Boolean(regions),
  target: checkUrl,
})

export const noRegionIdInUrl = createEvent()
export const checkIdFromUrl = createEvent<number | null>()
export const checkPreferredRegion = createEvent()

// check region id from url or triger logic if there are no region id in url
split({
  clock: checkUrl,
  source: $regionId,
  match: {
    regionIdInUrlExist: (regionId) => Boolean(regionId),
    noRegionIdInUrl: (regionId) => !regionId,
  },
  cases: {
    regionIdInUrlExist: checkIdFromUrl,
    noRegionIdInUrl: epgRegionsFeature.checkPreferredRegion,
  },
})

// URL region validation
// if region from url exists in the list of available regions, set it as active region
// if not - the logic for opening popup and reset region storage will be triggered in the epg widget resetting model
sample({
  clock: checkIdFromUrl,
  source: { regions: epgRegionsModel.$regionsAlphabeticallySorted },
  filter: ({ regions }, id) =>
    Boolean(regions?.find((region) => region.id === id)),
  fn: ({ regions }, id) => regions?.find((region) => region.id === id),
  target: selectEpgRegionModel.setRegionFromUrl,
})

sample({
  clock: selectEpgRegionModel.setRegionFromUrl,
  fn: (region) => region?.alias?.toLowerCase(),
  target: landingRegionFeatureModel.setLandingRegion,
})

sample({
  clock: [$regionId, landingRegionFeatureModel.$landingRegion],
  source: {
    preference: preferencesFeatureModel.$preference,
    landingRegion: landingRegionFeatureModel.$landingRegion,
    route: $route,
    regionId: $regionId,
  },
  filter: ({ preference, landingRegion, route, regionId }) => {
    return (
      Boolean(preference?.id) &&
      Boolean(landingRegion) &&
      route?.routeName === ROUTE_NAME.LIVE_CATEGORIES &&
      Boolean(regionId)
    )
  },
  fn: ({ preference, landingRegion }) => {
    const prefRegion = preference?.alias?.toLowerCase()
    return prefRegion !== landingRegion
  },
  target: landingRegionFeatureModel.setLandingNotificationPopupOpened,
})

// close landing notification popup on EPG/HOME page left
sample({
  clock: $route,
  fn: F,
  target: landingRegionFeatureModel.setLandingNotificationPopupOpened,
})

// Logic if user was authenticated and primary region was changed

const resetWasAuthenticated = createEvent()
export const $wasAuthenticated = createStore(false).reset(resetWasAuthenticated)
sample({
  clock: api.user.signInFx.done,
  source: $route,
  filter: (route) =>
    Boolean(route?.routeName?.includes(`${PATH.LIVE}${PATH.CATEGORIES}`)),
  fn: T,
  target: $wasAuthenticated,
})

sample({
  clock: [$wasAuthenticated, preferencesFeatureModel.$isDataReady],
  source: preferencesFeatureModel.$preference,
  filter: and($wasAuthenticated, preferencesFeatureModel.$isDataReady),
  target: [epgRegionsFeature.checkPreferredRegion, resetWasAuthenticated],
})

// logic for select region from popup

// if possible preffered region should be preselected, otherwise first region should be preselected
export const $preselectedRegion = combine(
  {
    preferenceRegion: preferencesFeatureModel.$preference,
    regionsAlphabeticallySorted: epgRegionsModel.$regionsAlphabeticallySorted,
  },
  ({ preferenceRegion, regionsAlphabeticallySorted }) => {
    return (
      regionsAlphabeticallySorted?.find(
        (region) => region.id === preferenceRegion?.id
      ) ||
      regionsAlphabeticallySorted?.[0] ||
      null
    )
  }
)

export const $regionsForPopup = combine(
  {
    preselectedRegion: $preselectedRegion,
    regionsAlphabeticallySorted: epgRegionsModel.$regionsAlphabeticallySorted,
  },
  ({ preselectedRegion, regionsAlphabeticallySorted }) => {
    if (!regionsAlphabeticallySorted || !preselectedRegion) return null

    // Filter out preselected region
    const otherRegions = regionsAlphabeticallySorted.filter(
      (region) => region.id !== preselectedRegion?.id
    )

    // Return preselected region first, followed by other regions
    return [preselectedRegion, ...otherRegions]
  }
)

export const epgRegionsForm = createForm({
  fields: {
    region: {
      init: null as { id: number; name: string; alias: string } | null,
    },
  },
})

// Preselect the region
sample({
  clock: [epgRegionsFeature.$isRegionsPopupOpened, $preselectedRegion],
  source: {
    preselectedRegion: $preselectedRegion,
    isRegionsPopupOpened: epgRegionsFeature.$isRegionsPopupOpened,
  },
  filter: ({ isRegionsPopupOpened, preselectedRegion }) =>
    isRegionsPopupOpened && Boolean(preselectedRegion),
  fn: ({ preselectedRegion }) => ({
    region: preselectedRegion?.id ? preselectedRegion : null,
  }),
  target: epgRegionsForm.setForm,
})

sample({
  clock: epgRegionsForm.submit,
  source: epgRegionsForm.fields.region.$value,
  target: [select, selectEpgRegionModel.setRegionFromPopup],
})
